import type { NodeType } from '../types';

export interface MediaDimensions {
  width: number;
  height: number;
  duration?: number;
}

type MediaNodeType = Extract<NodeType, 'image' | 'video' | 'audio'>;

const MEDIA_DIMENSIONS_LOAD_TIMEOUT_MS = 10_000;

function withLoadTimeout(promise: Promise<MediaDimensions | undefined>): Promise<MediaDimensions | undefined> {
  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      resolve(undefined);
    }, MEDIA_DIMENSIONS_LOAD_TIMEOUT_MS);
    promise.then((dimensions) => {
      window.clearTimeout(timeoutId);
      resolve(dimensions);
    }).catch(() => {
      window.clearTimeout(timeoutId);
      resolve(undefined);
    });
  });
}

export function loadMediaDimensionsFromSrc(
  type: MediaNodeType,
  src?: string
): Promise<MediaDimensions | undefined> {
  if (!src || type === 'audio') {
    return Promise.resolve(undefined);
  }

  let loadPromise: Promise<MediaDimensions | undefined>;
  if (type === 'image') {
    loadPromise = new Promise((resolve) => {
      const image = new window.Image();
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => resolve(undefined);
      image.src = src;
    });
  } else {
    loadPromise = new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        ...(Number.isFinite(video.duration) && video.duration > 0 ? { duration: video.duration } : {}),
      });
      video.onerror = () => resolve(undefined);
      video.src = src;
    });
  }

  return withLoadTimeout(loadPromise);
}

export function loadMediaDimensionsFromFile(
  file: File,
  type: MediaNodeType
): Promise<MediaDimensions | undefined> {
  if (type === 'audio') {
    return Promise.resolve(undefined);
  }

  const objectUrl = URL.createObjectURL(file);

  if (type === 'image') {
    return new Promise((resolve) => {
      const image = new window.Image();
      image.onload = () => {
        resolve({ width: image.naturalWidth, height: image.naturalHeight });
        URL.revokeObjectURL(objectUrl);
      };
      image.onerror = () => {
        resolve(undefined);
        URL.revokeObjectURL(objectUrl);
      };
      image.src = objectUrl;
    });
  }

  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      resolve({
        width: video.videoWidth,
        height: video.videoHeight,
        ...(Number.isFinite(video.duration) && video.duration > 0 ? { duration: video.duration } : {}),
      });
      URL.revokeObjectURL(objectUrl);
    };
    video.onerror = () => {
      resolve(undefined);
      URL.revokeObjectURL(objectUrl);
    };
    video.src = objectUrl;
  });
}

export function loadAudioDurationFromSrc(src: string): Promise<number | undefined> {
  if (!src) {
    return Promise.resolve(undefined);
  }

  return new Promise((resolve) => {
    const audio = document.createElement('audio');
    audio.preload = 'metadata';

    const cleanup = () => {
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.src = '';
    };

    audio.onloadedmetadata = () => {
      const duration = Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : undefined;
      cleanup();
      resolve(duration);
    };
    audio.onerror = () => {
      cleanup();
      resolve(undefined);
    };

    audio.src = src;
  });
}

export function loadAudioDurationFromFile(file: File): Promise<number | undefined> {
  const objectUrl = URL.createObjectURL(file);

  return loadAudioDurationFromSrc(objectUrl).finally(() => {
    URL.revokeObjectURL(objectUrl);
  });
}

export function loadVideoDurationFromSrc(src: string): Promise<number | undefined> {
  if (!src) {
    return Promise.resolve(undefined);
  }

  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onerror = null;
      video.src = '';
    };

    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : undefined;
      cleanup();
      resolve(duration);
    };
    video.onerror = () => {
      cleanup();
      resolve(undefined);
    };

    video.src = src;
  });
}

export function loadVideoDurationFromFile(file: File): Promise<number | undefined> {
  const objectUrl = URL.createObjectURL(file);

  return loadVideoDurationFromSrc(objectUrl).finally(() => {
    URL.revokeObjectURL(objectUrl);
  });
}
